import { RootState } from '../../../types/rootState'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCustomerProfile,
  selectCustomerPublicKey,
  selectHomebotNetworkEnabled,
  selectNativeAccessFeature
} from '../../../store/selectors/customerProfile'
import { selectScheduleTourShowModal } from '../../../store/slices/scheduleTour'
import ScheduleTourWizard from './ScheduleTour/ScheduleTourWizard'
import { RouteComponentProps, useLocation } from 'react-router'
import { setSelectedListingId } from '../../../actions/listings'
import { useTrackingContext } from '../../../providers/tracking'
import { setActiveNavigationTab } from '../../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES } from '../../../types/navigation'
import { SimilarHomes } from './SimilarHomes/SimilarHomes'
import { DownloadMobileAppPromptCard } from './DownloadMobileAppPromptCard/Desktop/DownloadMobileAppPromptCard'
import { HomeSearchFooter } from '../HomeSearchFooter/HomeSearchFooter'
import { Box, Flex, LoadingSpinner } from '@homebotapp/hb-react-component-catalog'
import { MobileDownloadMobileAppPromptCard } from './DownloadMobileAppPromptCard/Mobile/MobileDownloadMobileAppPromptCard'
import useViewport from '../../../hooks/useViewport'
import { Description } from './Description/Description'
import { HomeDetails } from './HomeDetails/HomeDetails'
import { LocationMap } from './LocationMap/LocationMap'
import { StatusHeader } from './StatusHeader/StatusHeader'
import { PropertyStats } from './PropertyStats/PropertyStats'
import { RealEstateAgentCard } from './RealEstateAgentCard/RealEstateAgentCard'
import { useGetListing } from '../../../hooks/listings/useGetListing'
import { EstimatedMonthlyPayment } from './EstimatedMonthlyPayment/EstimatedMonthlyPayment'
import { fetchLatestMortgageRates } from '../../../actions/latestMortgageRates'
import { formatStreetAddress } from '../../../helpers/formatListingAddress'
import { ListingDetailsPhotos } from './PhotoPanel/ListingDetailsPhotos/ListingDetailsPhotos'
import { ListingActionBar } from './ListingActionBar/ListingActionBar'
import { defineMessages, useIntl } from 'react-intl'
import { DownForMaintenance } from '../../shared/DownForMaintenance/DownForMaintenance'
import { HomeHighlights } from './HomeHighlights/HomeHighlights'
import { OpenHouseOrScheduleTour } from './OpenHouseOrScheduleTour/OpenHouseOrScheduleTour'
import { ListingMessageCardContainer } from './ListingMessageCard/ListingMessageCardContainer'
import { OwnAHome } from './OwnAHome/OwnAHome'
import { selectClientHomes, selectClientId } from '../../../store/selectors/client'
import { fetchClientHomes } from '../../../actions/client'
import {
  BUY_DOWN_TYPE,
  NO_CLEAR_BUY_DOWN_TYPE_FOUND
} from './EstimatedMonthlyPayment/EstimatedMonthlyPaymentBreakdown/EstimatedMonthlyPaymentBreakdownBuyDownToggle/BuyDownType'
import { MarketTemperatureCard } from './MarketTemperatureCard/MarketTemperatureCard'
import { selectClientIsGuest } from '../../../store/selectors/client'
import { ShortTermRental } from './ShortTermRental/ShortTermRental'
import { selectShouldShowAirDnaModule } from '../../../store/selectors/airDna'
import { Affordability } from './Affordability/Affordability'
import { calculateAssumableSavings } from './EstimatedMonthlyPayment/Utilities/CalculateAssumableSavings'
import { markListingAsSeen, isListingSeen } from '../Listings/ListingsMap/utils/seenListings'

export interface ListingDetailsMatchParams {
  listingId: string
}

const MSG = defineMessages({
  notFoundHead: {
    id: 'ListingDetails.notFoundHead',
    defaultMessage: 'This listing could not be found'
  },
  notFoundBody: {
    id: 'ListingDetails.notFoundBody',
    defaultMessage: 'It has most likely been removed from the MLS.'
  }
})

function scrollToTheTop(target?: HTMLElement | null) {
  requestAnimationFrame(() => {
    if (target) {
      target.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      return
    }

    // Fallback for window-scrolling pages (Safari quirks included)
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  })
}

export const ListingDetails = ({ match }: RouteComponentProps<ListingDetailsMatchParams>) => {
  const { listingId } = match.params
  const { isMediumOrAbove } = useViewport()
  const dispatch = useDispatch()
  const isGuest = useSelector(selectClientIsGuest)
  const publicKey = useSelector(selectCustomerPublicKey)
  const scrollerRef = useRef<HTMLDivElement>(null)

  // Setting omitSimilarListings to true will prevent fetching similar listings
  // They are fetched in the SimilarHomes component
  const { isLoading, data, refetch } = useGetListing(
    {
      listingId: listingId,
      options:
        isGuest && publicKey
          ? { customerPublicKey: publicKey, omitSimilarListings: true }
          : { omitSimilarListings: true }
    },
    {
      enabled: !!listingId
    }
  )
  const homes = useSelector(selectClientHomes)

  const listing = data?.listingEnriched?.listing
  const address = listing?.address && formatStreetAddress(listing.address)
  const loanInfo = data?.listingEnriched?.loanInfo
  const bucketedMonthlyHomeExpenses = data?.listingEnriched?.bucketedMonthlyHomeExpenses
  const assumableMortgageMonthlyHomeExpenses = data?.listingEnriched?.assumableMortgageMonthlyHomeExpenses
  const assumableMortgageLoanInfo = data?.listingEnriched?.assumableMortgageLoanInfo
  const assumableMortgageSecondLoanInfo = data?.listingEnriched?.assumableMortgageSecondLoanInfo
  const showModal = useSelector(selectScheduleTourShowModal)
  const hasNativeAccess = useSelector<RootState, boolean>(selectNativeAccessFeature)
  const { trackingClient } = useTrackingContext()
  const ampliEventRef = useRef(false)
  const propertyDescriptionRef = useRef<HTMLDivElement>(null)
  const estimatedMonthlyPaymentRef = useRef<HTMLDivElement>(null)
  const intl = useIntl()
  const isCustomerOnHomebotNetwork = useSelector(selectHomebotNetworkEnabled)
  const clientId = useSelector(selectClientId)
  const concessionAmount = listing?.concessionAmount
  const customerProfile = useSelector(selectCustomerProfile)

  const showAirDnaModule = useSelector(selectShouldShowAirDnaModule)
  const potentialAssumableSavings = useMemo(
    () =>
      calculateAssumableSavings({
        assumableBucketedMonthlyHomeExpenses: assumableMortgageMonthlyHomeExpenses,
        bucketedMonthlyHomeExpenses: bucketedMonthlyHomeExpenses
      }),
    [assumableMortgageMonthlyHomeExpenses, bucketedMonthlyHomeExpenses]
  )

  const [assumableToggle, setAssumableToggle] = useState(false)

  useEffect(() => {
    if (listing?.isAssumable && (potentialAssumableSavings ?? 0) > 0 && customerProfile?.isAssumableEnabled) {
      setAssumableToggle(true)
    }
  }, [listing?.isAssumable, potentialAssumableSavings, customerProfile?.isAssumableEnabled])

  // reset assumable toggle when navigating to new listing
  useEffect(() => {
    setAssumableToggle(false)
  }, [listingId])

  useEffect(() => {
    if (listingId && !isListingSeen(listingId)) {
      markListingAsSeen(listingId)
    }
  }, [listingId])

  const { pathname } = useLocation()

  useEffect(() => {
    if (clientId) {
      dispatch(fetchClientHomes(clientId))
    }
  }, [clientId, dispatch])

  useEffect(() => {
    if (!listingId) {
      return
    }

    refetch()
    scrollToTheTop(scrollerRef.current)
  }, [listingId, refetch])

  useEffect(() => {
    dispatch(fetchLatestMortgageRates())
  }, [dispatch])

  useEffect(() => {
    // Set the active tab to home search when we are on the
    // listing details page.
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.SEARCH))
    dispatch(setSelectedListingId(listing?.id))
  }, [dispatch, listing?.id])

  useEffect(() => {
    const canControlScrollRestoration = 'scrollRestoration' in window.history
    if (canControlScrollRestoration) {
      window.history.scrollRestoration = 'manual'
    }

    window.scrollTo(0, 0)
  }, [pathname])

  useEffect(() => {
    if (isLoading === false && ampliEventRef.current === false) {
      trackingClient?.listingViewed({
        building_size: listing?.sqftTotal,
        city: listing?.address?.city || undefined,
        mls_number: listing?.mlsNumber,
        num_baths: listing?.bathroomsCount || undefined,
        num_beds: listing?.bedroomsCount || undefined,
        price_per_sqft: listing?.priceCents
          ? Math.ceil(listing?.priceCents / 100 / (listing?.sqftTotal || listing?.sqftLot || 1))
          : undefined,
        price: listing?.priceCents ? listing?.priceCents / 100 : undefined,
        property_type: listing?.propertyType,
        source: listing?.originatingSystemName,
        state_or_province: listing?.address?.stateOrProvince,
        status: listing?.status,
        year_built: listing?.yearBuilt || undefined,
        zip_code: listing?.address?.postalCode
      })

      ampliEventRef.current = true
    }
  }, [listing, trackingClient])

  if (!listing?.id && !isLoading) {
    return (
      <DownForMaintenance head={intl.formatMessage(MSG.notFoundHead)} body={intl.formatMessage(MSG.notFoundBody)} />
    )
  }

  if (isLoading) {
    return (
      <Flex
        align='center'
        justify='center'
        h='calc(100vh - (var(--banner-height, 0px) + var(--top-nav-bar-height, 0px) + 48px))'
      >
        <LoadingSpinner variant='bot-head' size='lg' />
      </Flex>
    )
  }

  return (
    <>
      <Box
        bg='black'
        position='relative'
        ref={scrollerRef}
        style={{
          overflowY: 'auto',
          maxHeight: '100vh',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {listing && <ListingActionBar listing={listing} />}
        {listing && <ListingDetailsPhotos listing={listing} />}
        {/* @ts-ignore */}
        <Box padding={[2, 4]} background='var(--hb-colors-chakra-body-bg)'>
          <Box maxW='67rem' margin='auto' pl='env(safe-area-inset-left, 0)'>
            <Flex direction={['column', null, null, 'row']} justify='space-between' gap={4}>
              <Box flexBasis='66.66%'>
                <StatusHeader listing={listing} />
                <PropertyStats listing={listing} />
                <HomeHighlights
                  listing={listing}
                  assumableMortgageLoanInfo={assumableMortgageLoanInfo}
                  assumablePotentialSavings={calculateAssumableSavings({
                    assumableBucketedMonthlyHomeExpenses: assumableMortgageMonthlyHomeExpenses,
                    bucketedMonthlyHomeExpenses: bucketedMonthlyHomeExpenses
                  })}
                  estimatedMonthlyPaymentRef={estimatedMonthlyPaymentRef}
                  propertyDescriptionRef={propertyDescriptionRef}
                  setAssumableToggle={setAssumableToggle}
                />
                <Description listing={listing} propertyDescriptionRef={propertyDescriptionRef} />
                <Box mt={8} display={['block', null, null, 'none']}>
                  <OpenHouseOrScheduleTour listing={listing} />
                </Box>
                <Affordability price={listing?.priceCents ? listing?.priceCents / 100 : 0} />
                <Box ref={estimatedMonthlyPaymentRef}>
                  <EstimatedMonthlyPayment
                    address={address}
                    assumableMortgageLoanInfo={assumableMortgageLoanInfo}
                    assumableMortgageMonthlyHomeExpenses={assumableMortgageMonthlyHomeExpenses}
                    assumableMortgageSecondLoanInfo={assumableMortgageSecondLoanInfo}
                    assumableToggle={assumableToggle}
                    bucketedMonthlyHomeExpenses={bucketedMonthlyHomeExpenses}
                    listingHasBuyDown={listing?.hasBuyDownConcession}
                    listingBuyDownType={(listing?.buyDownType as BUY_DOWN_TYPE) || NO_CLEAR_BUY_DOWN_TYPE_FOUND}
                    concessionAmount={concessionAmount}
                    homePrice={listing?.priceCents ? listing?.priceCents / 100 : 0}
                    isAssumable={listing?.isAssumable}
                    listingId={listingId}
                    listingZipCode={listing?.address?.postalCode}
                    loanInfo={loanInfo}
                    setAssumableToggle={setAssumableToggle}
                  />
                </Box>

                {listing?.address?.latitude &&
                  listing?.address?.longitude &&
                  listing.internetAddressDisplayYn !== false &&
                  listing.showMapLink !== false && <LocationMap listing={listing} showHeading />}
                <HomeDetails listing={listing} />
                {data?.listingEnriched?.marketReport && (
                  <MarketTemperatureCard
                    marketData={data.listingEnriched.marketReport}
                    listingPrice={listing?.priceCents ? listing?.priceCents / 100 : 0}
                  />
                )}
                {/* !isPublic && <LoanOfficerCard listing={listing} /> */}
                {showAirDnaModule && listing && <ShortTermRental listing={listing} />}
              </Box>
              <Box
                left={2}
                right={2}
                zIndex={2}
                flexBasis='33.33%'
                position={['fixed', null, null, 'relative']}
                bottom={['calc(4rem + env(safe-area-inset-bottom))', null, null, 0]}
              >
                <Box display={['none', null, null, 'block']}>
                  <OpenHouseOrScheduleTour listing={listing} />
                </Box>
                {isCustomerOnHomebotNetwork && listing ? (
                  <ListingMessageCardContainer listing={listing} />
                ) : (
                  <RealEstateAgentCard listing={listing} />
                )}
              </Box>
            </Flex>
            {listingId && <SimilarHomes listingId={listingId} />}
            {hasNativeAccess && (
              <Box mt={8}>
                {isMediumOrAbove ? <DownloadMobileAppPromptCard /> : <MobileDownloadMobileAppPromptCard />}
              </Box>
            )}
            <HomeSearchFooter listing={listing} />
          </Box>
        </Box>
      </Box>
      {showModal && listing && <ScheduleTourWizard listing={listing} />}
    </>
  )
}
